import React, { useState, useEffect } from 'react'
import './Pagination.scss'

const LEFT_PAGE = 'LEFT'
const RIGHT_PAGE = 'RIGHT'

interface IPaginationProps {
  totalRecords: number
  pageLimit: number
  pageNeighbours?: number | 0
  currentPage: number
  onPageChanged: (object: { currentPage: number, totalPages: number, pageLimit: number, totalRecords: number }) => void
}

export type PaginationProps = IPaginationProps

/**
 * Helper method for creating a range of numbers
 * range(1, 5) => [1, 2, 3, 4, 5]
 */
const range = (from: number, to: number, step = 1): number[] => {
  let i = from
  const range = []

  while (i <= to) {
    range.push(i)
    i += step
  }

  return range
}

export const Pagination = (props: PaginationProps): JSX.Element => {
  const [pageLimit, setPageLimit] = useState(
    typeof props.pageLimit === 'number' ? props.pageLimit : 30
  )
  const [totalRecords, setTotalRecords] = useState(
    typeof props.totalRecords === 'number' ? props.totalRecords : 0
  )
  const [pageNeighbours, setPageNeighbours] = useState(
    typeof props.pageNeighbours === 'number'
      ? Math.max(0, Math.min(props.pageNeighbours, 2))
      : 0
  )

  const [totalPages, setTotalPages] = useState(
    Math.ceil(props.totalRecords / props.pageLimit)
  )
  const [currentPage, setCurrentPage] = useState(props.currentPage)

  useEffect(() => {
    setPageLimit(typeof props.pageLimit === 'number' ? props.pageLimit : 30)
    setTotalRecords(
      typeof props.totalRecords === 'number' ? props.totalRecords : 0
    )
    setPageNeighbours(
      typeof props.pageNeighbours === 'number'
        ? Math.max(0, Math.min(props.pageNeighbours, 2))
        : 0
    )

    setTotalPages(Math.ceil(props.totalRecords / props.pageLimit))

    setTotalPages(Math.ceil(props.totalRecords / props.pageLimit))
  }, [props])

  useEffect(() => {
    gotoPage(props.currentPage)
  }, [props.currentPage])

  const gotoPage = (page: number | string): void => {
    const { onPageChanged } = props

    const currentPage = Math.max(0, Math.min(Number(page), totalPages))

    const paginationData = {
      currentPage,
      totalPages,
      pageLimit,
      totalRecords
    }

    setCurrentPage(currentPage)

    onPageChanged(paginationData)
  }

  const handleClick = (page: number | string) => (evt: Event): void => {
    evt.preventDefault()
    gotoPage(page)
  }

  const handleMoveLeft = (evt: Event): void => {
    evt.preventDefault()
    gotoPage(currentPage - pageNeighbours * 2 - 1)
  }

  const handleMoveRight = (evt: Event): void => {
    evt.preventDefault()
    gotoPage(currentPage + pageNeighbours * 2 + 1)
  }

  /**
   * Let's say we have 10 pages and we set pageNeighbours to 2
   * Given that the current page is 6
   * The pagination control will look like the following:
   *
   * (1) < {4 5} [6] {7 8} > (10)
   *
   * (x) => terminal pages: first and last page(always visible)
   * [x] => represents current page
   * {...x} => represents page neighbours
   */
  const fetchPageNumbers = (): Array<string | number> => {
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbours * 2 + 3
    const totalBlocks = totalNumbers + 2

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours)
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours)

      let pages = range(startPage, endPage) as Array<string | number>

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2
      const hasRightSpill = totalPages - endPage > 1
      const spillOffset = totalNumbers - (pages.length + 1)

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1)
          pages = [LEFT_PAGE, ...extraPages, ...pages]
          break
        }

        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset)
          pages = [...pages, ...extraPages, RIGHT_PAGE]
          break
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE]
          break
        }
      }

      return [1, ...pages, totalPages]
    }

    return range(1, totalPages)
  }

  return !totalRecords || totalPages === 1
    ? null
    : (
    <>
      <ul className="pagination">
        {fetchPageNumbers().map((page: number | string, index) => {
          if (page === LEFT_PAGE) {
            return (
              <li key={index} className="page-item">
                <a
                  className="page-link"
                  href="#"
                  aria-label="Previous"
                  onClick={handleMoveLeft}
                >
                  <span aria-hidden="true">&laquo;</span>
                </a>
              </li>
            )
          }

          if (page === RIGHT_PAGE) {
            return (
              <li key={index} className="page-item">
                <a
                  className="page-link"
                  href="#"
                  aria-label="Next"
                  onClick={handleMoveRight}
                >
                  <span aria-hidden="true">&raquo;</span>
                </a>
              </li>
            )
          }

          return (
            <li
              key={index}
              className={`page-item${currentPage === page ? ' active' : ''}`}
            >
              <a className="page-link" href="#" onClick={handleClick(page)}>
                {page}
              </a>
            </li>
          )
        })}
      </ul>
    </>
      )
}
